import FieldValidator from '@/views/calculators/services/fieldValidatorService';
import { modalWithExit, togglePopup } from './AlertService';
import { exception } from '@/appinsights/logging/ApplicationInsightLog';
import { CommonModel } from '../types/CommonModel';
import { hasProductExistingInsurance } from './ProductService';
import store from '@/store/store';
import { BasketModel } from '../basket/BasketModel';

export interface ValuePair {
	value: string;
	displayValue: string;
}

export const monthYearArray: Array<ValuePair> = [
	{
		value: 'M',
		displayValue: 'Pr. md.',
	},
	{
		value: 'Y',
		displayValue: 'Pr. år',
	},
];

export const jaNejArray: Array<ValuePair> = [
	{
		value: 'ja',
		displayValue: 'Ja',
	},
	{
		value: 'nej',
		displayValue: 'Nej',
	},
];

export const validNowArray: Array<ValuePair> = [
	{
		value: 'ja',
		displayValue: 'Hurtigst muligt',
	},
	{
		value: 'nej',
		displayValue: 'Vælg dato',
	},
];

export const formatNumber: Intl.NumberFormat = Intl.NumberFormat('da-DK', {
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
} as any);

export const formatPercent: Intl.NumberFormat = Intl.NumberFormat('da-DK', {
	style: 'percent',
	minimumFractionDigits: 0,
} as any);

export const Validator: FieldValidator = new FieldValidator('none');

export const getShowReceipt = (comp) => {
	return comp.componentInit && comp.cardReceipt && (comp.model?.showReceipt || comp.checkout);
};

export const isValidAlmbrandProducts = (model) => {
	let ok = model.personInfo.almbrandCustomer !== undefined;
	if (ok) {
		if (model.personInfo.almbrandCustomer === 'ja') {
			ok = model.personInfo.existingAlmBrandProducts?.length > 0 || !model.showExistingAlmBrandProducts;
		}
	}
	return ok;
};

export const isValidPersonInfo = (model): boolean => {
	const okPerson =
		model.personInfo.zipCode &&
		model.personInfo.zipName &&
		Validator.isValidAge(model.personInfo.customerAge) &&
		isValidAlmbrandProducts(model);
	if (okPerson) {
		model.personInfo.customerAge = model.personInfo.customerAge.trim().replace(/\D/g, '');
		model.personInfo.subtitle = `${model.personInfo.customerAge} år, ${model.personInfo.zipCode} ${model.personInfo.zipName}`;
	}
	return okPerson;
};

export const isValidOtherPersonInfo = (model): boolean => {
	const okPerson =
		model.personInfo.zipCode &&
		model.personInfo.zipName &&
		Validator.isValidAge(model.personInfo.otherCustomerAge) &&
		isValidAlmbrandProducts(model);
	if (okPerson) {
		model.personInfo.otherCustomerAge = model.personInfo.otherCustomerAge.trim().replace(/\D/g, '');
		model.personInfo.subtitle = `${model.personInfo.otherCustomerAge} år, ${model.personInfo.zipCode} ${model.personInfo.zipName}`;
	}
	return okPerson;
};

export const isValidContactInfo = (model, cms, basket = false): boolean => {
	if (cms.customerAddressRequired && model.personInfo.address === undefined) {
		return false;
	}

	if (!model.contact_information.acceptConsentCpr) {
		return false;
	}

	let validOtherCompanyConsent = false;
	if (basket) {
		validOtherCompanyConsent = hasBasketValidConsent(model);
	} else {
		validOtherCompanyConsent = hasProductValidConsent(model);
	}
	if (model.contact_information.customerNo) {
		return (
			!!validOtherCompanyConsent &&
			Validator.isValidEmail(model.contact_information.email) &&
			Validator.isValidPhone(model.contact_information.phone)
		);
	}
	const okContact =
		validOtherCompanyConsent &&
		Validator.isValidName(model.contact_information.name) &&
		Validator.isValidPhone(model.contact_information.phone) &&
		Validator.isValidEmail(model.contact_information.email) &&
		Validator.isValidCpr(model.contact_information.cpr);
	return !!okContact;
};

const hasBasketValidConsent = (model: BasketModel) => {
	if (model?.contact_information) {
		if (!model.contact_information.acceptOtherCompanyConsent) {
			let existingInsurance = false;
			const basket = store.getters.getSelectableGroup('basket');
			basket.forEach((product) => {
				const calc = store.getters.getCalculator(product);
				if (!existingInsurance && calc?.model?.readyForBasket && calc?.model?.contact_information) {
					existingInsurance = hasProductExistingInsurance(calc.model);
				}
			});
			return !existingInsurance;
		}
	} else {
		return false;
	}
	return true;
};

const hasProductValidConsent = (model: CommonModel) => {
	if (model?.contact_information) {
		if (!model.contact_information.acceptOtherCompanyConsent) {
			return !hasProductExistingInsurance(model);
		}
	} else {
		return false;
	}
	return true;
};

export const isValidAdditionalInfo = (skipInsuranceFields: boolean, model, cms): boolean => {
	let ok = true;
	if (!skipInsuranceFields) {
		ok = !!(model.additionalInfo.existInsurance && model.additionalInfo.validNow);
		if (!ok) {
			return ok;
		}
		if (model.additionalInfo.existInsurance === 'ja') {
			ok = Validator.isValidExistingInsurance(model.additionalInfo.existingInsurance);
			if (!ok) {
				return ok;
			}
		}
	}
	if (!model.additionalInfo.validNow) {
		return false;
	}
	if (model.additionalInfo.validNow === 'nej') {
		ok = Validator.isValidFromDate(model.additionalInfo.validFromDate);
	}

	if (ok && !model.multipleProducts) {
		// if in basketMode, RKI and damages question will be in another flow
		ok = model.additionalInfo.rki === 'nej';
		if (ok) {
			ok = model.additionalInfo.skader && cms.damagesOk.includes(model.additionalInfo.skader);
		}
	}

	return !!ok;
};

export const isValidChildrenCpr = (model): boolean => {
	let ok = true;
	if (model?.familyInfo?.personCountId > -1) {
		for (let i = 0; i <= model.familyInfo.personCountId; i++) {
			const cpr = 'cpr' + i;
			if (
				ok &&
				(model.familyInfo[cpr] === undefined || Validator.getAgeUnderEighteen(model.familyInfo[cpr]) < 0)
			) {
				ok = false;
			}
		}
	}

	return ok;
};

export const isValidPaymentInfo = (model, cms): boolean => {
	if (cms.skipAccountInfo) {
		return model.payment.userSelected && !!model.choosePackage.monthYear;
	}
	return (
		model.payment.userSelected &&
		!!model.choosePackage.monthYear &&
		Validator.isValidRegNo(model.payment.regNo) &&
		Validator.isValidKontoNo(model.payment.kontoNo)
	);
};

export const isValidPackage = (comp): boolean => {
	const ok = !!(comp.model.choosePackage.selectedPackage && comp.model.choosePackage.ownRiskId);
	if (ok && !comp.model.overview.highlights) {
		comp.setOverviewData();
	}
	return ok;
};

export const handleValidCustomerAge = (model, cms): boolean => {
	const ok = Validator.isValidAge(model.personInfo.customerAge);
	if (!ok) {
		const modal = modalWithExit(cms);
		modal.title = cms.customerAgePopupTitle;
		modal.content = cms.customerAgePopupContent;
		modal.id = 'customerAgePopup';
		togglePopup(cms, modal);
	}
	// clean customer age input
	model.personInfo.customerAge = model.personInfo.customerAge.trim().replace(/\D/g, '');
	return ok;
};

/**Checks if calculator has Address supplement  (price * 1.6)*/
export const hasAddressSupplement = (model, cms): boolean => {
	try {
		const zipCitySearch = `${model.personInfo.zipCode} ${model.personInfo.zipName}`.toLocaleLowerCase();
		const zipFound = cms.callUpAddressesBlocks.find((address) => address.zipCity === zipCitySearch);
		if (zipFound) {
			const streetName = model.personInfo.streetName.toLocaleLowerCase();
			const streetFound = zipFound.streets.find((street) => street.name === streetName);
			if (streetFound) {
				if (streetFound.nos) {
					const no = model.personInfo.houseNr.toLocaleLowerCase();
					return streetFound.nos.includes(no);
				}
			} else {
				return false;
			}
			return true;
		}
		return false;
	} catch (error) {
		exception(error);
		return false;
	}
};

export const showValidNowOption = (model): boolean => {
	if (
		model.additionalInfo.existInsurance === 'nej' ||
		(model.additionalInfo.existInsurance === 'ja' && model.additionalInfo.existingInsurance)
	) {
		return true;
	}
	return false;
};

const regexNumbersAndLetters = /[^a-zA-ZæøåÆØÅ0-9]+/g;
export const onlyNumbersAndLetters = (value: string): string => {
	if (value) {
		return value.replace(regexNumbersAndLetters, '');
	}
	return value;
};

const regexNumbersLettersAndSymbols = /[^a-zA-Z\æøåØÆÅ0-9@\+\.,\-_\s]+/g;
export const cleanInput = (value: string): string => {
	if (value) {
		return value.replace(regexNumbersLettersAndSymbols, '');
	}
	return value;
};
